import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress,
    Stack,
    Chip,
    alpha,
    Dialog,
    Button,
    FormControlLabel,
    Checkbox,
    Slider,
    fabClasses
} from '@mui/material';
import { useRef, useEffect, useState, useCallback } from "react";
import { useSelector } from 'react-redux';
import { drawGRZ, drawGeometryLines, drawMarkLines } from "../../../utils/skats";
import md5 from "md5";
import { skatTimeToString } from "../../../utils/converters";

// Иконки
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GridOnIcon from '@mui/icons-material/GridOn';
import TimelineIcon from '@mui/icons-material/Timeline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

const ComplexViewSkatS = ({ complex, type = 'default' }) => {
    const isDarkMode = useSelector(state => state.settings.darkMode);

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const ws = useRef(null);
    
    const [isLoading, setIsLoading] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [showSettings, setShowSettings] = useState(true);
    const [authAttempt, setAuthAttempt] = useState(1); // 1 = первый пароль, 2 = второй пароль
    const generatedKeyRef = useRef(null); // Сохраняем generated_key для повторной попытки

    // Трансформация изображения (зум, перемещение)
    const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
    const [isPanning, setIsPanning] = useState(false);
    const panStart = useRef({ x: 0, y: 0 });

    const [config, setConfig] = useState({
        geometry: false,
        marking: true,
        grz: false,
        k: 0.7,
        q: 30,
        width_cam: 1168,
        height_cam: 600,
        pass_hash_1: 'ec6b5c0313ea1ce19624455f798ed289',
        pass_hash_2: '1a1dc91c907325c69271ddf0c944bc72',
        message: {
            method: "authentication",
            params: {
                name: 'Администратор',
                stage: 1
            }
        }
    });

    const [data, setData] = useState({
        lat: '--.-',
        lon: '--.-',
        time: '--.-',
        volt: '--.-',
        temp: '--',
        fps: 0,
        frameCount: 0
    });

    const frameCountRef = useRef(0);
    const lastFpsUpdateRef = useRef(Date.now());

    // Подключение к WebSocket СКАТ-С
    const connect = useCallback(() => {
        if (!complex?.interfaces?.[1]?.ip) {
            setError('Отсутствует IP-адрес для подключения');
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            setAuthAttempt(1); // Сбрасываем попытку авторизации
            generatedKeyRef.current = null;
            
            let URL = "wss://map.infosys05.ru/ws?complex=" + complex.interfaces[1].ip;
            if (window.location.protocol === 'http:') {
                URL = "ws://" + complex.interfaces[1].ip + ":7681";
            }
            
            console.log('СКАТ-С: Подключение к', URL);
            ws.current = new WebSocket(URL);
            
            ws.current.onopen = () => {
                console.log("СКАТ-С: Соединение установлено");
                setIsConnected(true);
                const message = {
                    method: "authentication",
                    params: {
                        name: 'Администратор',
                        stage: 1
                    },
                    id: 1
                };
                ws.current.send(JSON.stringify(message));
            };
            
            ws.current.onclose = (event) => {
                console.log('СКАТ-С: Соединение закрыто', event.code, event.reason);
                setIsConnected(false);
                setIsLoading(true);
                if (!event.wasClean) {
                    setError('Соединение разорвано');
                }
            };

            ws.current.onerror = (error) => {
                console.error('СКАТ-С: Ошибка WebSocket', error);
                setError('Ошибка подключения к СКАТ-С');
                setIsLoading(true);
            };

        } catch (e) {
            console.error('СКАТ-С: Ошибка', e);
            setError('Не удалось подключиться');
            setIsLoading(true);
        }
    }, [complex?.interfaces]);

    // Отключение
    const disconnect = useCallback(() => {
        if (ws.current) {
            ws.current.close();
            ws.current = null;
        }
        setIsConnected(false);
        setIsLoading(true);
    }, []);

    // Инициализация подключения
    useEffect(() => {
        connect();
        return () => disconnect();
    }, [connect, disconnect]);

    // Обработка сообщений WebSocket
    useEffect(() => {
        if (!ws.current) return;

        ws.current.onmessage = (event) => {
            const e = JSON.parse(event.data);
            
            if (e.id === 1) {
                // Сохраняем generated_key для возможной повторной попытки
                generatedKeyRef.current = e.result.generated_key;
                
                // Выбираем хэш пароля в зависимости от попытки
                const currentHash = authAttempt === 1 ? config.pass_hash_1 : config.pass_hash_2;
                const key = md5(e.result.generated_key + currentHash);
                
                console.log(`СКАТ-С: Попытка авторизации #${authAttempt}`);
                
                const message = {
                    ...config.message,
                    params: {
                        ...config.message.params,
                        stage: 2,
                        authorize_key: key
                    },
                    id: 2
                };
                ws.current.send(JSON.stringify(message));
            }
            
            if (e.id === 2) {
                // Проверяем успешность авторизации
                if (e.error) {
                    console.log('СКАТ-С: Ошибка авторизации', e.error);
                    
                    if (authAttempt === 1) {
                        // Первый пароль не подошёл, пробуем второй
                        console.log('СКАТ-С: Пробуем второй пароль...');
                        setAuthAttempt(2);
                        
                        // Отправляем повторный запрос авторизации с вторым паролем
                        const key = md5(generatedKeyRef.current + config.pass_hash_2);
                        const message = {
                            ...config.message,
                            params: {
                                ...config.message.params,
                                stage: 2,
                                authorize_key: key
                            },
                            id: 2
                        };
                        ws.current.send(JSON.stringify(message));
                        return;
                    } else {
                        // Оба пароля не подошли
                        setError('Ошибка авторизации: неверный пароль');
                        setIsLoading(true);
                        return;
                    }
                }
                
                console.log('СКАТ-С: Авторизация успешна');
                ws.current.send(JSON.stringify({ method: "get_info", id: 3 }));
            }
            
            if (e.id === 3) {
                setData(prev => ({
                    ...prev,
                    info: e.result,
                    lat: e.result.gns?.latitude || prev.lat,
                    lon: e.result.gns?.longitude || prev.lon,
                    volt: e.result.volt || prev.volt,
                    temp: e.result.temp || prev.temp
                }));
                ws.current.send(JSON.stringify({ method: "get_uconfig", id: 4 }));
            }
            
            if (e.id === 4) {
                setData(prev => ({
                    ...prev,
                    uconfig: e.result
                }));
                ws.current.send(JSON.stringify({ method: "get_mconfig", id: 5 }));
            }
            
            if (e.id === 5) {
                const frameWidth = e.result.camera?.frame_width || 1920;
                const frameHeight = e.result.camera?.frame_height || 1080;
                
                setData(prev => ({
                    ...prev,
                    mconfig: e.result,
                    width_cam: frameWidth,
                    height_cam: frameHeight
                }));
                
                setConfig(prev => ({
                    ...prev,
                    width_cam: Math.round(frameWidth * prev.k),
                    height_cam: Math.round(frameHeight * prev.k)
                }));
                
                // Запрашиваем первый кадр
                const message = {
                    id: 6,
                    method: "get_frame",
                    params: {
                        quality: config.q,
                        size: { width: Math.round(frameWidth * config.k), height: Math.round(frameHeight * config.k) },
                        fit: true,
                        with_recogn: true
                    }
                };
                ws.current.send(JSON.stringify(message));
            }
            
            if (e.id === 6) {
                setData(prev => ({
                    ...prev,
                    time: skatTimeToString(e.result.time),
                    stage: 6
                }));
                setIsLoading(false);
                
                // Обновляем FPS
                frameCountRef.current++;
                const now = Date.now();
                if (now - lastFpsUpdateRef.current >= 1000) {
                    setData(prev => ({
                        ...prev,
                        fps: frameCountRef.current,
                        frameCount: prev.frameCount + frameCountRef.current
                    }));
                    frameCountRef.current = 0;
                    lastFpsUpdateRef.current = now;
                }
                
                // Рисуем на canvas
                if (!isPaused && canvasRef.current) {
                    const ctx = canvasRef.current.getContext("2d");
                    const base_image = new Image();
                    base_image.src = e.result.image.data;
                    base_image.onload = function() {
                        ctx.drawImage(base_image, 0, 0);
                        
                        if (config.geometry && data.uconfig && data.mconfig) {
                            drawGeometryLines(
                                ctx, config.k,
                                data.uconfig.mount_markup_points,
                                data.width_cam, data.height_cam,
                                data.mconfig.camera.aov_h,
                                data.mconfig.camera.aov_v,
                                data.uconfig.mount_rotation,
                                data.uconfig.mount_slope
                            );
                        }
                        
                        if (config.marking && data.uconfig?.zones_markup?.lines) {
                            drawMarkLines(ctx, 0, config.k, data.uconfig.zones_markup.lines);
                        }
                        
                        if (config.grz && e.result.recogn?.length > 0) {
                            drawGRZ(ctx, config.k, e.result.recogn);
                        }
                        
                        ctx.stroke();
                    };
                }
                
                // Запрашиваем следующий кадр если не на паузе
                if (!isPaused && ws.current?.readyState === WebSocket.OPEN) {
                    const message = {
                        id: 6,
                        method: "get_frame",
                        params: {
                            quality: config.q,
                            size: { width: config.width_cam, height: config.height_cam },
                            fit: true,
                            with_recogn: true
                        }
                    };
                    ws.current.send(JSON.stringify(message));
                }
            }
        };
    }, [config, data, isPaused, authAttempt]);

    // Пауза/воспроизведение
    const togglePause = useCallback(() => {
        if (isPaused && ws.current?.readyState === WebSocket.OPEN) {
            const message = {
                id: 6,
                method: "get_frame",
                params: {
                    quality: config.q,
                    size: { width: config.width_cam, height: config.height_cam },
                    fit: true,
                    with_recogn: true
                }
            };
            ws.current.send(JSON.stringify(message));
        }
        setIsPaused(!isPaused);
    }, [isPaused, config]);

    // Обработка колесика мыши (зум)
    const handleWheel = useCallback((e) => {
        if (isLoading) return;
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setTransform(prev => ({
            ...prev,
            scale: Math.min(5, Math.max(0.5, prev.scale * delta))
        }));
    }, [isLoading]);

    // Обработка перетаскивания
    const handleMouseDown = useCallback((e) => {
        if (isLoading || transform.scale <= 1) return;
        e.preventDefault();
        setIsPanning(true);
        panStart.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
    }, [isLoading, transform]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isPanning) return;
            setTransform(prev => ({
                ...prev,
                x: e.clientX - panStart.current.x,
                y: e.clientY - panStart.current.y
            }));
        };
        const handleMouseUp = () => setIsPanning(false);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isPanning]);

    const resetTransform = useCallback(() => {
        setTransform({ x: 0, y: 0, scale: 1 });
    }, []);

    // Горячие клавиши
    useEffect(() => {
        const handleKeyDown = (e) => {
            switch (e.code) {
                case 'Space':
                    e.preventDefault();
                    togglePause();
                    break;
                case 'Escape':
                    setFullscreen(false);
                    break;
                default:
                    break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [togglePause]);

    // Проверка наличия необходимых данных
    if (!complex?.interfaces?.[1]?.ip) {
        return (
            <Box sx={{
                p: 4,
                textAlign: 'center',
                color: 'var(--text-muted)',
                backgroundColor: 'var(--background-default)',
                borderRadius: '12px',
                border: '1px solid var(--border-default)'
            }}>
                <VideocamOffIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                    СКАТ-С недоступен
                </Typography>
                <Typography sx={{ fontSize: '0.85rem' }}>
                    Отсутствует IP-адрес для подключения к комплексу
                </Typography>
            </Box>
        );
    }

    // Рендер видео
    const renderVideoViewer = (isFullscreen = false) => (
        <Box
            ref={!isFullscreen ? containerRef : null}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            sx={{
                flex: 1,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isFullscreen ? '#0d1117' : 'var(--background-default)',
                borderRadius: isFullscreen ? 0 : '8px',
                overflow: 'hidden',
                cursor: isPanning ? 'grabbing' : (transform.scale > 1 ? 'grab' : 'default'),
                userSelect: 'none',
                position: 'relative',
                minHeight: isFullscreen ? 0 : { xs: 250, md: 350 }
            }}
        >
            {isLoading ? (
                <Box sx={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    <CircularProgress size={36} sx={{ color: 'var(--primary-color)' }} />
                    <Typography sx={{ mt: 1.5, fontSize: '0.8rem' }}>
                        Подключение к СКАТ-С...
                    </Typography>
                </Box>
            ) : error ? (
                <Box sx={{ textAlign: 'center', color: 'var(--error-color)' }}>
                    <ErrorOutlineIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.7 }} />
                    <Typography sx={{ fontSize: '0.85rem', mb: 1 }}>
                        {error}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={connect}
                        sx={{ fontSize: '0.75rem' }}
                    >
                        Переподключиться
                    </Button>
                </Box>
            ) : (
                <Box
                    sx={{
                        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                        transition: isPanning ? 'none' : 'transform 0.1s ease-out'
                    }}
                >
                    <canvas
                        ref={canvasRef}
                        width={config.width_cam}
                        height={config.height_cam}
                        onClick={togglePause}
                        style={{
                            maxWidth: isFullscreen ? '95vw' : '100%',
                            maxHeight: isFullscreen ? '80vh' : 'calc(60vh - 120px)',
                            borderRadius: isFullscreen ? 0 : 8,
                            boxShadow: isFullscreen ? 'none' : '0 4px 24px rgba(0,0,0,0.2)',
                            cursor: 'pointer'
                        }}
                    />
                </Box>
            )}
        </Box>
    );

    // Рендер панели управления
    const renderControls = (isFullscreen = false) => {
        const buttonStyle = {
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            border: `1px solid var(--border-default)`,
            borderRadius: { xs: '4px', md: '6px' },
            color: 'var(--text-secondary)',
            backgroundColor: isDarkMode ? 'var(--background-paper)' : 'transparent',
            '&:hover': {
                backgroundColor: 'var(--hover-bg)',
                borderColor: 'var(--primary-color)'
            },
            '&:disabled': { opacity: 0.3 }
        };

        const buttonStyleFullscreen = {
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: { xs: '4px', md: '6px' },
            color: 'rgba(255,255,255,0.9)',
            backgroundColor: 'rgba(255,255,255,0.05)',
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderColor: 'rgba(255,255,255,0.4)'
            },
            '&:disabled': { opacity: 0.3 }
        };

        const currentButtonStyle = isFullscreen ? buttonStyleFullscreen : buttonStyle;

        return (
            <Box sx={{
                p: { xs: 1, md: 1.5 },
                backgroundColor: isFullscreen ? 'rgba(13, 17, 23, 0.95)' : 'var(--background-subtle)',
                borderTop: `1px solid ${isFullscreen ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`,
                gap: 2,
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                {/* Статистика */}
                <Stack 
                    direction="row" 
                    spacing={{ xs: 0.5, md: 1.5 }} 
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', gap: { xs: 0.5, md: 0 } }}
                >
                    {isConnected && !isLoading && (
                        <>
                            <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                                label={data.time || '--:--:--'}
                                size="small"
                                sx={{
                                    height: { xs: 20, md: 22 },
                                    fontSize: { xs: '0.6rem', md: '0.7rem' },
                                    backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                                    color: 'var(--primary-color)',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                    '& .MuiChip-label': { px: { xs: 0.5, md: 1 } }
                                }}
                            />
                            <Chip
                                icon={<GpsFixedIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                                label={`${Number(data.lat).toFixed(4)}, ${Number(data.lon).toFixed(4)}`}
                                size="small"
                                sx={{
                                    height: { xs: 20, md: 22 },
                                    fontSize: { xs: '0.6rem', md: '0.7rem' },
                                    backgroundColor: alpha(isDarkMode ? '#3fb950' : '#1a7f37', 0.15),
                                    color: 'var(--success-color)',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                    '& .MuiChip-label': { px: { xs: 0.5, md: 1 } },
                                    display: { xs: 'none', sm: 'flex' } // Скрываем на очень маленьких экранах
                                }}
                            />
                            <Chip
                                icon={<BatteryChargingFullIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                                label={`${Number(data.volt).toFixed(1)}V`}
                                size="small"
                                sx={{
                                    height: { xs: 20, md: 22 },
                                    fontSize: { xs: '0.6rem', md: '0.7rem' },
                                    backgroundColor: alpha(isDarkMode ? '#d29922' : '#9a6700', 0.15),
                                    color: 'var(--warning-color)',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                    '& .MuiChip-label': { px: { xs: 0.5, md: 1 } }
                                }}
                            />
                            <Chip
                                icon={<ThermostatIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                                label={`${data.temp}°C`}
                                size="small"
                                sx={{
                                    height: { xs: 20, md: 22 },
                                    fontSize: { xs: '0.6rem', md: '0.7rem' },
                                    backgroundColor: alpha(isDarkMode ? '#f85149' : '#cf222e', 0.15),
                                    color: 'var(--error-color)',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                    '& .MuiChip-label': { px: { xs: 0.5, md: 1 } }
                                }}
                            />
                        </>
                    )}
                </Stack>

                {/* Кнопки управления */}
                <Stack direction="row" spacing={{ xs: 0.5, md: 1 }} alignItems="center">
                    <Tooltip title={isPaused ? "Продолжить (Пробел)" : "Пауза (Пробел)"}>
                        <IconButton
                            size="small"
                            onClick={togglePause}
                            disabled={isLoading}
                            sx={{
                                ...currentButtonStyle,
                                color: isPaused ? 'var(--success-color)' : 'var(--warning-color)',
                                borderColor: alpha(isPaused ? (isDarkMode ? '#3fb950' : '#1a7f37') : (isDarkMode ? '#d29922' : '#9a6700'), 0.5),
                                '&:hover': {
                                    backgroundColor: alpha(isPaused ? (isDarkMode ? '#3fb950' : '#1a7f37') : (isDarkMode ? '#d29922' : '#9a6700'), 0.15),
                                    borderColor: isPaused ? 'var(--success-color)' : 'var(--warning-color)'
                                }
                            }}
                        >
                            {isPaused ? <PlayArrowIcon sx={{ fontSize: { xs: 16, md: 18 } }} /> : <PauseIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
                        </IconButton>
                    </Tooltip>

                    <Box sx={{ width: { xs: 6, md: 12 } }} />

                    <Tooltip title="Приблизить">
                        <IconButton
                            size="small"
                            onClick={() => setTransform(prev => ({ ...prev, scale: Math.min(5, prev.scale * 1.2) }))}
                            disabled={isLoading}
                            sx={currentButtonStyle}
                        >
                            <ZoomInIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Отдалить">
                        <IconButton
                            size="small"
                            onClick={() => setTransform(prev => ({ ...prev, scale: Math.max(0.5, prev.scale * 0.8) }))}
                            disabled={isLoading}
                            sx={currentButtonStyle}
                        >
                            <ZoomOutIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                        </IconButton>
                    </Tooltip>

                    {transform.scale !== 1 && (
                        <Tooltip title="Сбросить масштаб">
                            <IconButton
                                size="small"
                                onClick={resetTransform}
                                sx={{
                                    ...currentButtonStyle,
                                    color: 'var(--primary-color)',
                                    borderColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.5),
                                    '&:hover': {
                                        backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.15),
                                        borderColor: 'var(--primary-color)'
                                    }
                                }}
                            >
                                <ZoomOutMapIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Box sx={{ width: { xs: 6, md: 12 } }} />

                    <Tooltip title="Настройки отображения">
                        <IconButton
                            size="small"
                            onClick={() => setShowSettings(!showSettings)}
                            sx={{
                                ...currentButtonStyle,
                                ...(showSettings && {
                                    color: 'var(--primary-color)',
                                    borderColor: 'var(--primary-color)',
                                    backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1)
                                })
                            }}
                        >
                            <SettingsIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                        </IconButton>
                    </Tooltip>

                    {!isFullscreen ? (
                        <Tooltip title="Полный экран">
                            <IconButton
                                size="small"
                                onClick={() => setFullscreen(true)}
                                disabled={isLoading}
                                sx={currentButtonStyle}
                            >
                                <FullscreenIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Закрыть">
                            <IconButton
                                size="small"
                                onClick={() => setFullscreen(false)}
                                sx={{
                                    ...buttonStyleFullscreen,
                                    borderColor: 'rgba(248, 81, 73, 0.5)',
                                    color: '#f85149',
                                    '&:hover': {
                                        backgroundColor: 'rgba(248, 81, 73, 0.2)',
                                        borderColor: '#f85149'
                                    }
                                }}
                            >
                                <CloseIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Box>
        );
    };

    // Рендер настроек
    const renderSettings = () => (
        showSettings && (
            <Box sx={{
                p: { xs: 0.75, md: 1.5 },
                backgroundColor: 'var(--background-subtle)',
                borderBottom: '1px solid var(--border-default)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 0.5, md: 2 },
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {/* Чекбоксы */}
                <Stack 
                    direction="row" 
                    spacing={{ xs: 0.5, md: 1.5 }} 
                    alignItems="center"
                    sx={{ flexWrap: 'wrap' }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={config.geometry}
                                onChange={(e) => setConfig(prev => ({ ...prev, geometry: e.target.checked }))}
                                sx={{
                                    p: { xs: 0.25, md: 0.5 },
                                    color: 'var(--text-secondary)',
                                    '&.Mui-checked': { color: 'var(--primary-color)' }
                                }}
                            />
                        }
                        label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <GridOnIcon sx={{ color: 'var(--text-secondary)', fontSize: { xs: 14, md: 16 } }} />
                                <Typography color='var(--text-secondary)' sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                                    Наведение
                                </Typography>
                            </Stack>
                        }
                        sx={{ m: 0, mr: { xs: 0.5, md: 1 } }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={config.marking}
                                onChange={(e) => setConfig(prev => ({ ...prev, marking: e.target.checked }))}
                                sx={{
                                    p: { xs: 0.25, md: 0.5 },
                                    color: 'var(--text-secondary)',
                                    '&.Mui-checked': { color: 'var(--primary-color)' }
                                }}
                            />
                        }
                        label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <TimelineIcon sx={{ color: 'var(--text-secondary)', fontSize: { xs: 14, md: 16 } }} />
                                <Typography color='var(--text-secondary)' sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                                    Разметка
                                </Typography>
                            </Stack>
                        }
                        sx={{ m: 0, mr: { xs: 0.5, md: 1 } }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={config.grz}
                                onChange={(e) => setConfig(prev => ({ ...prev, grz: e.target.checked }))}
                                sx={{
                                    p: { xs: 0.25, md: 0.5 },
                                    color: 'var(--text-secondary)',
                                    '&.Mui-checked': { color: 'var(--primary-color)' }
                                }}
                            />
                        }
                        label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <DirectionsCarIcon sx={{ color: 'var(--text-secondary)', fontSize: { xs: 14, md: 16 } }} />
                                <Typography color='var(--text-secondary)' sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                                    ГРЗ
                                </Typography>
                            </Stack>
                        }
                        sx={{ m: 0 }}
                    />
                </Stack>

                {/* Слайдеры */}
                <Stack 
                    direction="row" 
                    spacing={{ xs: 1, md: 2 }} 
                    alignItems="center"
                    sx={{ flexWrap: 'wrap' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: 120, md: 150 } }}>
                        <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                            Масштаб: {config.k}
                        </Typography>
                        <Slider
                            size="small"
                            value={config.k}
                            min={0.2}
                            max={1}
                            step={0.1}
                            onChange={(e, value) => setConfig(prev => ({
                                ...prev,
                                k: value,
                                width_cam: Math.round((data.width_cam || 1920) * value),
                                height_cam: Math.round((data.height_cam || 1080) * value)
                            }))}
                            sx={{ width: { xs: 60, md: 80 }, color: 'var(--primary-color)' }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: 120, md: 150 } }}>
                        <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                            Качество: {config.q}%
                        </Typography>
                        <Slider
                            size="small"
                            value={config.q}
                            min={10}
                            max={100}
                            step={10}
                            onChange={(e, value) => setConfig(prev => ({ ...prev, q: value }))}
                            sx={{ width: { xs: 60, md: 80 }, color: 'var(--primary-color)' }}
                        />
                    </Box>
                </Stack>
            </Box>
        )
    );

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: 'var(--background-paper)',
            overflow: 'hidden',
            border: '1px solid var(--border-default)'
        }}>
            {/* Заголовок */}
            <Box sx={{
                p: { xs: 1, md: 1.5 },
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, md: 1.5 },
                borderBottom: '1px solid var(--border-default)',
                backgroundColor: 'var(--background-subtle)'
            }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <CameraAltIcon sx={{ fontSize: 18, color: 'var(--primary-color)' }} />
                    <Typography sx={{
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: 'var(--text-secondary)'
                    }}>
                        СКАТ-С
                    </Typography>
                </Stack>

                <Typography
                    component="a"
                    href={`http://${complex.interfaces[1].ip}`}
                    target="_blank"
                    sx={{
                        fontSize: '0.7rem',
                        color: 'var(--primary-color)',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    {complex.interfaces[1].ip} ↗
                </Typography>

                {isConnected && !isLoading && (
                    <Chip
                        label={isPaused ? "ПАУЗА" : "LIVE"}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            backgroundColor: isPaused ? 'var(--warning-color)' : 'var(--error-color)',
                            color: '#fff',
                            animation: isPaused ? 'none' : 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.7 }
                            }
                        }}
                    />
                )}
            </Box>

            {/* Настройки */}
            {renderSettings()}

            {/* Область просмотра */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
                {renderVideoViewer()}
            </Box>

            {/* Управление */}
            <Box sx={{ flexShrink: 0 }}>
                {renderControls()}
            </Box>

            {/* Полноэкранный режим */}
            <Dialog
                open={fullscreen}
                onClose={() => setFullscreen(false)}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: '#0d1117',
                        m: 0,
                        maxHeight: '100vh',
                        height: '100vh',
                        borderRadius: 0,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    width: '100%',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        {renderVideoViewer(true)}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                        {renderControls(true)}
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default ComplexViewSkatS;
